// Waitlist.jsx
import React, { useState, useEffect } from "react";
import { campanaVigente } from "../utils/campana";
import "./Waitlist.css";

const DOWNLOAD_URL = "https://wayoai.app/download?utm_source=website&utm_medium=qr&utm_campaign=download_modal";
const DOWNLOAD_BUTTON_URL = "https://wayoai.app/download?utm_source=website&utm_medium=button&utm_campaign=download_modal";

// WebP y a 510px de ancho. Los PNG originales median 1080px y pesaban 439 KB
// entre los dos, para pintarse a 255px (190 en movil): cuatro veces mas grandes
// de lo que se ven. En WebP al doble del tamano de pantalla son 130 KB.
const FOTO_IZQUIERDA = "/assets/img/imagen-modal3.webp";
const FOTO_DERECHA = "/assets/img/imagen-modal2.webp";

// El logo era un PNG de 1536x1024 y 135 KB para pintarse a 120px. En WebP a
// 320px son 11 KB.
const LOGO = "/assets/img/wayo-logo.webp";

// El QR codifica una URL FIJA, asi que no hace falta pedirselo a nadie. Antes se
// traia de api.qrserver.com en el momento de abrir el modal: una peticion a un
// tercero que empezaba en el segundo 10 y que, si su servicio va lento o se cae,
// deja el modal sin QR. Ahora es un fichero de 674 bytes servido con el resto.
const QR = "/assets/img/qr-download-modal.png";

// —— Campaña "Vuelta a la rutina" (septiembre 2026) ——
// El boton lleva a /download, o sea a la tienda, que es justo donde el precio SI
// esta bajado. Por eso este modal si puede anunciar la oferta, al contrario que
// el resto de la web, que cobra por Stripe (29,99 € desde el 5-sep-2026).
// La fecha de cierre es la de `utils/campana.js`: tenerla copiada aqui era pedir
// que un dia se apagara la barra y este modal siguiera anunciando la oferta.

// Cuánto se calla el modal después de que lo cierres. Sin esto volvía a saltar
// a los 10 segundos en CADA pagina: lo cerrabas, entrabas al blog y ahí estaba
// otra vez. Un modal que no recuerda que le has dicho que no es de las cosas
// que más queman una visita.
const SILENCIO_MS = 7 * 24 * 60 * 60 * 1000; // una semana
const CLAVE_CERRADO = "wayo_modal_descarga_cerrado";

const loCerroHacePoco = () => {
  try {
    const cuando = Number(window.localStorage.getItem(CLAVE_CERRADO));
    return Boolean(cuando) && Date.now() - cuando < SILENCIO_MS;
  } catch {
    // Navegación privada o almacenamiento bloqueado: que se comporte como antes.
    return false;
  }
};

export default function Waitlist() {
  const [showPopup, setShowPopup] = useState(false);

  const campanaViva = campanaVigente();

  // Timer para mostrar popup después de 10 segundos
  useEffect(() => {
    if (loCerroHacePoco()) return undefined;

    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 10000); // 10 segundos

    return () => clearTimeout(timer);
  }, []);

  // Los cuatro recursos del modal se piden por adelantado, porque el componente
  // devuelve null durante 10 segundos: sus <img> no estan en el DOM y sin esto
  // el navegador no empieza a bajarlos hasta el instante de ensenarlos, y el
  // modal se abre con huecos en blanco.
  //
  // ⚠️ Pero NO al instante y NO con prioridad normal. Arrancarlos a la vez que
  // el resto de la pagina les robaba ancho de banda a las imagenes que el
  // usuario SI esta mirando, y la portada se notaba mas lenta. Van con
  // fetchPriority "low" y esperan a que el navegador este ocioso (o a 3s si no
  // llega ese momento): siguen teniendo 7 segundos de sobra para descargarse.
  useEffect(() => {
    let cancelado = false;
    const precargar = () => {
      if (cancelado) return;
      [FOTO_IZQUIERDA, FOTO_DERECHA, LOGO, QR].forEach((src) => {
        const img = new Image();
        img.fetchPriority = "low";
        img.src = src;
      });
    };

    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(precargar, { timeout: 3000 })
      : window.setTimeout(precargar, 3000);

    return () => {
      cancelado = true;
      if (window.cancelIdleCallback && window.requestIdleCallback) {
        window.cancelIdleCallback(idle);
      } else {
        window.clearTimeout(idle);
      }
    };
  }, []);

  const closePopup = () => {
    setShowPopup(false);
    try {
      window.localStorage.setItem(CLAVE_CERRADO, String(Date.now()));
    } catch {
      // Si no se puede guardar, el modal volverá: mejor eso que romper el cierre.
    }
  };

  if (!showPopup) {
    return null;
  }

  return (
    <>
      <div 
        className="waitlist-popup-overlay" 
        onClick={closePopup}
      >
        <div 
          className="waitlist-popup"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="download-modal-title"
        >
          <button 
            onClick={closePopup}
            className="waitlist-popup-close"
            aria-label="Cerrar"
          >
            ×
          </button>

          <div className="waitlist-download-modal">
            <div className="waitlist-download-visual">
              <img
                src={FOTO_IZQUIERDA}
                alt="Wayo en el móvil"
                width="510"
                height="638"
                className="waitlist-download-phone waitlist-download-phone-left"
              />
              <img
                src={FOTO_DERECHA}
                alt="Wayo en el móvil"
                width="510"
                height="638"
                className="waitlist-download-phone waitlist-download-phone-right"
              />
            </div>

            <div className="waitlist-download-info">
              <div className="waitlist-download-topline">
                <div className="waitlist-download-brand">
                  <img src={LOGO} alt="Wayo" width="120" height="80" />
                </div>
              </div>

              {/* Lidera la NOTICIA, no la oferta: es lo que hace que alguien se
                  descargue la app hoy. La campaña baja a una línea y se cae sola
                  el 30 de septiembre. */}
              <div className="waitlist-download-copy">
                <span className="waitlist-download-nuevo">Nuevo</span>
                <h2 id="download-modal-title">
                  Ahora le hablas
                  <br />
                  y te apunta la comida
                </h2>
                <p className="waitlist-download-oferta">
                  Sin foto, sin buscar en listas y sin pesar nada.
                </p>
                {campanaViva && (
                  <p className="waitlist-download-campana">
                    Y hasta el 30, Premium casi a mitad de precio
                  </p>
                )}
              </div>

              <div className="waitlist-download-qr">
                <img src={QR} alt="QR para descargar Wayo" width="260" height="260" />
              </div>

              <p className="waitlist-download-scan">Escanea aquí</p>
            </div>

            <a
              className="waitlist-download-mobile-button"
              href={DOWNLOAD_BUTTON_URL}
              onClick={closePopup}
            >
              {campanaViva ? "Probar gratis y aprovechar la oferta" : "Probar 7 días gratis"}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
